import { Component, inject, input } from '@angular/core';
import { Microsoft365UserRef } from '../../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { TranslateService } from '@ngx-translate/core';
import { timer } from 'rxjs';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

type MsSearchResultType = 'user' | 'callQueue' | 'emergencyLocation' | 'autoAttendant';
type SearchResultColumn = 'name' | 'upn' | 'number';

export interface MicrosoftUserUiResult {
  ref: Microsoft365UserRef;
  lineUri: string;
  lineUriExtension: string;
  photo: string;
}

@Component({
  selector: 'ziro-microsoft-search-result',
  templateUrl: './microsoft-search-result.component.html',
  styleUrls: ['./microsoft-search-result.component.scss'],
  standalone: false,
})
export class MicrosoftSearchResultComponent {
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _translateService = inject(TranslateService);

  readonly result = input<Microsoft365UserRef>();
  readonly lineUri = input<string>();
  readonly lineUriExtension = input<string>();
  readonly photo = input<string>();
  readonly isLoadingLineUri = input(true);
  readonly showUpn = input<boolean>(true);
  readonly showSubLabel = input<boolean>(true);
  readonly subLabel = input<string>();
  readonly customIcon = input<ZiroIcons>();
  readonly showSearchResultIcon = input<boolean>(true);
  readonly hideAssignedNumberCol = input<boolean>(false);
  readonly charLimitUpn = input<number>(35);
  readonly initials = input<string | null>(null);
  readonly isPhotoLoading = input<boolean>(false);
  readonly isDisabled = input<boolean>(false);
  readonly disabledTooltip = input<string>('');
  msResultType = input<MsSearchResultType>(null);

  protected readonly ButtonTypes = ButtonTypes;
  protected readonly ButtonSizes = ButtonSizes;
  protected readonly ButtonStyles = ButtonStyles;
  protected readonly charLimitDN = 30;

  ziroIcons = ZiroIcons;
  copyTooltip: string;

  formatLineUri(lineUri: string, extension: string): string {
    if (lineUri?.length) {
      if (extension) {
        return this._telephoneNumberFilter.transform(lineUri) + ' ext.' + extension;
      }
      return this._telephoneNumberFilter.transform(lineUri);
    }
    return '';
  }

  handleHover(elType: SearchResultColumn): void {
    if (this.msResultType() === 'user') {
      if (elType === 'name') {
        this.copyTooltip = this._translateService.instant('tkey;search.copy.cell', {
          cellType: 'Display Name',
        });
      } else if (elType === 'upn') {
        this.copyTooltip = this._translateService.instant('tkey;search.copy.cell', {
          cellType: 'User Principal Name',
        });
      } else {
        this.copyTooltip = this._translateService.instant('tkey;search.copy.cell', {
          cellType: 'Assigned Number',
        });
      }
    } else if (this.msResultType() === 'callQueue') {
      this.copyTooltip = this._translateService.instant('tkey;search.copy.cell', {
        cellType: 'Call Queue Name',
      });
    } else if (this.msResultType() === 'autoAttendant') {
      this.copyTooltip = this._translateService.instant('tkey;search.copy.cell', {
        cellType: 'Auto Attendant Name',
      });
    } else {
      if (this.showSubLabel()) {
        this.copyTooltip = this._translateService.instant('tkey;search.copy.cell', {
          cellType: 'Emergency Place Name',
        });
      } else {
        this.copyTooltip = this._translateService.instant('tkey;search.copy.cell', {
          cellType: 'Emergency Location Name',
        });
      }
    }
  }

  handleCopy(e: MouseEvent, tooltip: NgbTooltip): void {
    e.stopPropagation();
    timer(1000).subscribe(() => {
      tooltip.close();
    });
  }

  calcNumberCopyValue(): string {
    let copyVal;
    if (this.lineUri() && !this.lineUriExtension()) {
      copyVal = this.lineUri();
    } else if (this.lineUriExtension && !this.lineUri) {
      copyVal = this.lineUriExtension();
    } else if (this.lineUri() && this.lineUriExtension()) {
      copyVal = `${this.lineUri()}  ext.${this.lineUriExtension()}`;
    }
    return copyVal;
  }
}
